<template>
  <div>
    <div class="wEditor" ref="wEditor"></div>
  </div>
</template>
<script>
import uploader from "@/utils/uploader";
import GetSource from "./menus/get-source";
import wangEditor from "wangeditor";
export default {
  name: "ElEditor",
  props: {
    value: {
      type: String,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "default",
    },
    height: {
      type: Number,
      default: 300,
    },
  },
  watch: {
    value(newValue) {
      this.initContent(newValue);
    },
    disable(newVal) {
      this.handleDisable(newVal, this.editor);
    },
    height(newVal) {
      this.handleHeight(newVal, this.editor);
    },
  },
  data() {
    return {
      editor: null,
      doUpading: false,
      doInit: false,
    };
  },
  mounted() {
    // 初始化
    this.initEditor();
  },
  beforeDestroy() {
    this.editor.destroy();
    this.editor = null;
  },
  methods: {
    // 初始化编辑器
    initEditor() {
      const editor = new wangEditor(this.$refs.wEditor);
      editor.menus.extend("getSource", GetSource);
      if (this.type === "simple") {
        editor.config.menus = ["bold", "head", "fontSize", "italic", "underline", "strikeThrough", "list", "link"];
      } else if (this.type === "pure") {
        editor.config.menus = ["bold", "head", "fontSize", "italic", "underline", "strikeThrough", "list", "link", "indent", "justify", "quote"];
      } else {
        editor.config.menus = [
          "bold",
          "head",
          "fontName",
          "fontSize",
          "italic",
          "underline",
          "strikeThrough",
          "indent",
          "foreColor",
          "backColor",
          "list",
          "justify",
          "quote",
          "image",
          "link",
          "table",
          "getSource",
        ];
      }
      editor.config.fontSizes = {
        "x-small": { name: "12px", value: "1" },
        small: { name: "14px", value: "2" },
        normal: { name: "16px", value: "3" },
        large: { name: "18px", value: "4" },
        "x-large": { name: "24px", value: "5" },
        "xx-large": { name: "32px", value: "6" },
        "xxx-large": { name: "48px", value: "7" },
      };
      // 字体选择列表: "英文名, 中文名"双名写法, face 属性按逗号回退, 兼容中英文系统
      editor.config.fontNames = [
        "SimHei, 黑体",
        "FangSong, 仿宋",
        "KaiTi, 楷体",
        "DFKai-SB, BiauKai, 标楷体",
        "STFangsong, 华文仿宋",
        "STKaiti, 华文楷体",
        "SimSun, 宋体",
        "Microsoft YaHei, 微软雅黑",
        "Times New Roman",
        "Arial",
        "Tahoma",
        "Verdana",
      ];
      // 判断是否禁用
      this.handleDisable(this.disable, editor);
      // 设置高度
      this.handleHeight(this.height, editor);

      editor.config.zIndex = 500;
      editor.config.showMenuTooltips = false;
      editor.config.focus = false;
      editor.config.placeholder = "";
      editor.config.showLinkImg = false;
      editor.config.uploadImgShowBase64 = true;
      editor.config.uploadImgMaxSize = 10 * 1024 * 1024;
      editor.config.customUploadImg = function (resultFiles, insertImgFn) {
        uploader
          .upload(resultFiles[0])
          .then(res => {
            const imgUrl = res.data.urls[0].url;
            insertImgFn(imgUrl);
          })
          .catch(err => {
            console.debug(err);
          });
      };
      editor.config.onchange = newHtml => {
        this.handleInput(newHtml);
      };
      editor.create();
      this.editor = editor;
    },
    // 禁用
    handleDisable(val, editor) {
      if (val) {
        editor.disable();
      } else {
        editor.enable();
      }
    },
    //设置高度
    handleHeight(val, editor) {
      editor.config.height = val;
    },
    // 监听输入
    handleInput(value) {
      if (!this.doInit) {
        console.debug("监听到输入");
        this.doUpading = true;
        this.$emit("input", this.formatTableBorder(value));
      }
      this.doInit = false;
    },
    // 为表格补充内联边框样式: wangEditor 插入的表格 border=0, 编辑器内的表格线来自其自带 CSS(不随 html 输出),
    // 输出的内容在邮件/页面中渲染时无边框, 故输出前内联边框样式 (邮件客户端会剥离 style 标签, 必须内联);
    // 负向前瞻保证幂等: 仅处理无 style 属性的标签, 回填再编辑不会重复叠加
    formatTableBorder(html) {
      return html
        .replace(/<table(?![^>]*style=)/g, '<table style="border-collapse:collapse;"')
        .replace(/<(td|th)(?![^>]*style=)/g, '<$1 style="border:1px solid #D0D3D6;padding:3px 5px;"');
    },
    // 初始化内容
    initContent(value) {
      // console.debug(this.doUpading, "新值:" + value || "空", "旧值:" + this.editor.txt.html() || "空");
      if (!this.doUpading) {
        this.editor.destroy();
        this.editor = null;
        this.initEditor();
        this.doInit = true;
        this.editor.txt.html(value);
      }
      this.doUpading = false;
    },
    // 插入html
    insertHtml(content) {
      this.editor.cmd.do("insertHTML", content);
    },
  },
};
</script>

<style lang="scss">
.wEditor {
  z-index: 1999 !important;
  & > div {
    border: 1px solid #ededed !important;
  }
  .w-e-text {
    // 全局 _normalize.scss 清掉了 ul/ol 的 list-style, wangEditor 注入样式只恢复了 margin 未恢复符号,
    // 此处局部恢复: 否则编辑器内有列表无序号、无序列表无圆点 (输出 HTML 与邮件端不受影响)
    ol {
      list-style: decimal;
      padding-left: 20px;
    }
    ul {
      list-style: disc;
      padding-left: 20px;
    }
    table {
      tr {
        vertical-align: top;
      }
    }
  }
  // 字体下拉面板宽度放开(插件内联写死 100px, 双名字体名较长会被截断)
  .w-e-droplist {
    width: auto !important;
    min-width: 100px;
    white-space: nowrap;
  }
}
</style>
